/**
 * User management API adapter for eos-app (v2 with org support)
 *
 * Reason: Organization-scoped user management for admins to manage
 * users within their organization
 */

import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import ApiV2 from './api-v2';

export const LIST_ORG_USERS_CACHE_KEY = 'list-org-users';

export interface OrgUser {
  id: string;
  orgId: string;
  email: string;
  name?: string;
  role: 'admin' | 'manager' | 'viewer';
  status: 'invited' | 'accepted' | 'declined' | 'expired';
  userId?: string;
  createdAt: string;
  updatedAt: string;
  // Legacy field for backward compatibility
  isActive?: boolean;
}

export interface CreateUserRequest {
  email: string;
  name?: string;
  role: 'admin' | 'manager' | 'viewer';
  isActive?: boolean;
}

export interface InviteUserRequest {
  email: string;
  name?: string;
  role?: 'admin' | 'manager' | 'viewer';
}

export interface UpdateUserRequest {
  name?: string;
  role?: 'admin' | 'manager' | 'viewer';
  isActive?: boolean;
  // Note: Status is managed by backend only, not updated from frontend
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Lists all users in the organization
 *
 * @reason Allows admins to view and manage all users in their org
 * @reason orgId is sent via x-org-id header (automatically added by ApiV2)
 */
export function useListOrgUsers(orgId?: string): UseQueryResult<OrgUser[]> {
  return useQuery(
    [LIST_ORG_USERS_CACHE_KEY, orgId],
    async () => {
      if (!orgId) {
        return [];
      }
      const response = await ApiV2.get<ApiResponse<OrgUser[]>>('/api/users');
      return response.data || [];
    },
    {
      enabled: !!orgId, // Only run if orgId is available
    },
  );
}

/**
 * Gets a specific user by ID
 *
 * @reason Retrieves detailed user information for editing
 */
export function useGetOrgUser(orgId?: string, userId?: string): UseQueryResult<OrgUser | null> {
  return useQuery(
    ['org-user', orgId, userId],
    async () => {
      if (!orgId || !userId) {
        return null;
      }
      const response = await ApiV2.get<ApiResponse<OrgUser>>(`/api/users/${userId}`);
      return response.data || null;
    },
    {
      enabled: !!orgId && !!userId,
    },
  );
}

/**
 * Invites a new user to the organization
 *
 * @reason Allows admins to invite new users to their org via invitation
 */
export function useInviteOrgUser(
  orgId?: string,
): UseMutationResult<OrgUser, unknown, InviteUserRequest, unknown> {
  return useMutation(async (userData: InviteUserRequest) => {
    if (!orgId) {
      throw new Error('Organization ID is required to invite users');
    }
    const response = await ApiV2.post<ApiResponse<OrgUser>>(
      `/api/orgs/${orgId}/invitations`,
      userData,
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to invite user');
    }
    return response.data;
  });
}

/**
 * Creates a new user in the organization
 *
 * @reason Allows admins to invite/add new users to their org
 */
export function useCreateOrgUser(): UseMutationResult<
  OrgUser,
  unknown,
  CreateUserRequest,
  unknown
> {
  return useMutation(async (userData: CreateUserRequest) => {
    const response = await ApiV2.post<ApiResponse<OrgUser>>('/api/users', userData);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create user');
    }
    return response.data;
  });
}

/**
 * Updates user details (role, active status, etc)
 *
 * @reason Allows admins to change user roles and activate/deactivate users
 */
export function useUpdateOrgUser(
  userId: string,
): UseMutationResult<void, unknown, UpdateUserRequest, unknown> {
  return useMutation(async (updates: UpdateUserRequest) => {
    const response = await ApiV2.patch<ApiResponse<void>>(`/api/users/${userId}`, updates);
    if (!response.success) {
      throw new Error(response.error || 'Failed to update user');
    }
  });
}

/**
 * Deactivates a user (soft delete)
 *
 * @reason Allows admins to remove users from the org without deleting data
 */
export function useDeactivateOrgUser(): UseMutationResult<void, unknown, string, unknown> {
  return useMutation(async (userId: string) => {
    const response = await ApiV2.patch<ApiResponse<void>>(`/api/users/${userId}`, {
      isActive: false,
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to deactivate user');
    }
  });
}

/**
 * Reactivates a deactivated user
 *
 * @reason Allows admins to restore access for previously deactivated users
 */
export function useReactivateOrgUser(): UseMutationResult<void, unknown, string, unknown> {
  return useMutation(async (userId: string) => {
    const response = await ApiV2.patch<ApiResponse<void>>(`/api/users/${userId}`, {
      isActive: true,
    });
    if (!response.success) {
      throw new Error(response.error || 'Failed to reactivate user');
    }
  });
}
